import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { colors, baseStyles } from '../styles';
import { SearchBar, PlaceCard, ProviderCard, AgregarLugarModal, LocationPrompt, LocationIndicator } from '../components';
import { categoriasMascota, categoriasObjeto } from '../data/fakeData';
import { getPlaces } from '../services/placesService';
import { resolveStoredLocation, saveLocation } from '../services/locationService';
import { getFavorites, addFavorite, removeFavorite } from '../services/favoritesService';

const styles = {
  ...baseStyles,
  resultCount: {
    fontSize: '0.875rem',
    color: colors.textSecondary,
    marginBottom: '1rem',
  },
  emptyState: {
    textAlign: 'center',
    padding: '3rem 1rem',
  },
  emptyIcon: {
    fontSize: '3rem',
    marginBottom: '1rem',
    color: colors.textMuted,
  },
  emptyTitle: {
    fontSize: '1rem',
    fontWeight: 600,
    color: colors.textPrimary,
    marginBottom: '0.5rem',
  },
  emptyText: {
    fontSize: '0.875rem',
    color: colors.textSecondary,
  },
};

// Helper para obtener el nombre de la categoría
const getNombreCategoria = (categoriaId) => {
  const todasCategorias = [
    ...categoriasMascota.establecimientos,
    ...categoriasMascota.proveedores,
    ...categoriasObjeto.establecimientos,
  ];
  const cat = todasCategorias.find(c => c.id === categoriaId);
  return cat?.nombre || categoriaId;
};

export default function LocalizadorResultados() {
  const navigate = useNavigate();
  const { categoria } = useParams();
  const [searchParams] = useSearchParams();
  const perfilId = searchParams.get('perfil');
  const [searchQuery, setSearchQuery] = useState('');
  const [lugares, setLugares] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showAgregarModal, setShowAgregarModal] = useState(false);

  // Estado de ubicación
  const [userLocation, setUserLocation] = useState(null);
  const [showLocationPrompt, setShowLocationPrompt] = useState(false);

  // Estado de favoritos
  const [favoritos, setFavoritos] = useState({}); // { placeId: favoritoId }
  const [favoritosCount, setFavoritosCount] = useState(0);
  const [favoritosLimit, setFavoritosLimit] = useState(3);
  const [favoritosPlan, setFavoritosPlan] = useState('light');
  const [favoritoError, setFavoritoError] = useState(null);

  const nombreCategoria = getNombreCategoria(categoria);

  // Cargar ubicación guardada al montar (resuelve nombre si es GPS)
  useEffect(() => {
    resolveStoredLocation().then(location => {
      if (location) {
        setUserLocation(location);
      } else {
        setShowLocationPrompt(true);
      }
    });
  }, []);

  // Cargar favoritos del usuario
  useEffect(() => {
    async function cargarFavoritos() {
      try {
        // Obtener favoritos CON metadata (count, limit, plan)
        const response = await getFavorites(null, true);
        const favMap = {};
        (response.data || []).forEach(fav => {
          favMap[fav.placeId] = fav.id;
        });
        setFavoritos(favMap);
        setFavoritosCount(response.count || Object.keys(favMap).length);
        setFavoritosLimit(response.limit || 3);
        setFavoritosPlan(response.plan || 'light');
      } catch (err) {
        console.log('Error cargando favoritos (puede ser usuario no autenticado):', err.message);
      }
    }
    cargarFavoritos();
  }, []);

  // Manejar toggle de favorito
  const handleToggleFavorite = async (placeId, isFavorito, favoritoId) => {
    setFavoritoError(null);

    try {
      if (isFavorito && favoritoId) {
        // Quitar de favoritos
        await removeFavorite(favoritoId);
        setFavoritos(prev => {
          const newFavs = { ...prev };
          delete newFavs[placeId];
          return newFavs;
        });
        setFavoritosCount(prev => prev - 1);
      } else {
        // Agregar a favoritos
        const result = await addFavorite(placeId, perfilId || 'general');
        setFavoritos(prev => ({ ...prev, [placeId]: result.id }));
        setFavoritosCount(prev => prev + 1);
      }
    } catch (err) {
      console.error('Error toggle favorito:', err);
      // Verificar si es error de limite
      if (err.message.includes('limite') || err.message.includes('limit')) {
        const isPremium = favoritosPlan === 'premium';
        const errorMsg = isPremium
          ? 'Has alcanzado el limite de favoritos.'
          : `Limite de favoritos alcanzado (${favoritosCount}/${favoritosLimit}). Mejora tu plan para guardar mas.`;
        setFavoritoError(errorMsg);
        // Limpiar error despues de 4 segundos
        setTimeout(() => setFavoritoError(null), 4000);
      } else {
        setFavoritoError(err.message);
        setTimeout(() => setFavoritoError(null), 3000);
      }
    }
  };

  // Manejar cuando el usuario establece su ubicación
  const handleLocationSet = (location) => {
    setUserLocation(location);
    setShowLocationPrompt(false);
  };

  // Manejar cambio de ubicación
  const handleChangeLocation = () => {
    setShowLocationPrompt(true);
  };

  // Cargar lugares desde Firestore (solo si hay ubicación)
  const cargarLugares = useCallback(async () => {
    // No buscar si no hay ubicación
    if (!userLocation) {
      setLugares([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);
    try {
      // Solo mostrar lugares verificados en el frontend de usuario
      const filtros = {
        categoria: categoria,
        status: 'verificado',
        lat: userLocation.lat,
        lng: userLocation.lng
      };
      if (searchQuery.trim()) {
        filtros.busqueda = searchQuery.trim();
      }
      if (userLocation.ciudad) {
        filtros.ciudad = userLocation.ciudad;
      }
      console.log('[LocalizadorResultados] Buscando con filtros:', filtros);
      const data = await getPlaces(filtros);
      setLugares(data);
    } catch (err) {
      console.error('Error cargando lugares:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [categoria, searchQuery, userLocation]);

  // Cargar al montar y cuando cambia la categoría, búsqueda o ubicación
  useEffect(() => {
    const debounceTimer = setTimeout(() => {
      cargarLugares();
    }, searchQuery ? 300 : 0); // Debounce para búsqueda

    return () => clearTimeout(debounceTimer);
  }, [cargarLugares, searchQuery]);

  // Separar establecimientos y proveedores
  const establecimientos = lugares.filter(l => l.tipo === 'establecimiento');
  const proveedores = lugares.filter(l => l.tipo === 'proveedor' || l.tipo === 'independiente');

  const handleBack = () => {
    navigate(-1);
  };

  const getResultText = () => {
    if (!userLocation) return 'Indica tu ubicación para ver resultados';
    if (loading) return 'Buscando cerca de ti...';
    const count = lugares.length;
    if (count === 0) return 'Sin resultados en tu zona';
    if (count === 1) return '1 resultado cerca de ti';
    return `${count} resultados cerca de ti`;
  };

  return (
    <div style={styles.container}>
      <div style={styles.main}>
        {/* Header */}
        <div style={styles.header}>
          <button style={styles.backButton} onClick={handleBack}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={colors.textPrimary} strokeWidth="2">
              <path d="M19 12H5M12 19l-7-7 7-7"/>
            </svg>
          </button>
          <div>
            <h1 style={styles.title}>{nombreCategoria}</h1>
          </div>
        </div>

        {/* Barra de búsqueda */}
        <SearchBar
          placeholder={`Buscar en ${nombreCategoria.toLowerCase()}...`}
          value={searchQuery}
          onChange={setSearchQuery}
        />

        {/* Indicador de ubicación */}
        <LocationIndicator
          location={userLocation}
          onChangeClick={handleChangeLocation}
        />

        {/* Contador de resultados */}
        <p style={styles.resultCount}>{getResultText()}</p>

        {/* Error */}
        {error && (
          <div style={styles.emptyState}>
            <div style={styles.emptyIcon}>⚠️</div>
            <h3 style={styles.emptyTitle}>Error al cargar</h3>
            <p style={styles.emptyText}>{error}</p>
            <button
              style={{ ...styles.backButton, marginTop: '1rem', padding: '0.5rem 1rem', borderRadius: '8px', backgroundColor: colors.accent, color: '#fff', border: 'none' }}
              onClick={cargarLugares}
            >
              Reintentar
            </button>
          </div>
        )}

        {/* Sin ubicación - mostrar mensaje para seleccionar */}
        {!userLocation && !loading && (
          <div style={styles.emptyState}>
            <div style={styles.emptyIcon}>📍</div>
            <h3 style={styles.emptyTitle}>Necesitamos tu ubicación</h3>
            <p style={styles.emptyText}>
              Para mostrarte lugares cercanos, indica dónde te encuentras.
            </p>
            <button
              onClick={handleChangeLocation}
              style={{
                marginTop: '1rem',
                padding: '0.75rem 1.5rem',
                backgroundColor: colors.accent,
                color: '#fff',
                border: 'none',
                borderRadius: '10px',
                fontSize: '0.875rem',
                fontWeight: 500,
                cursor: 'pointer'
              }}
            >
              📍 Indicar mi ubicación
            </button>
          </div>
        )}

        {/* Loading */}
        {userLocation && loading && (
          <div style={styles.emptyState}>
            <div style={styles.emptyIcon}>⏳</div>
            <p style={styles.emptyText}>Buscando lugares cercanos...</p>
          </div>
        )}

        {/* Sin resultados en la zona */}
        {userLocation && !loading && !error && lugares.length === 0 ? (
          <div style={styles.emptyState}>
            <div style={styles.emptyIcon}>🔍</div>
            <h3 style={styles.emptyTitle}>No hay resultados en tu zona</h3>
            <p style={styles.emptyText}>
              No encontramos {nombreCategoria.toLowerCase()} a menos de 5 km.
            </p>
            <button
              onClick={handleChangeLocation}
              style={{
                marginTop: '1rem',
                padding: '0.75rem 1.5rem',
                backgroundColor: colors.surface,
                color: colors.textPrimary,
                border: `1px solid ${colors.border}`,
                borderRadius: '10px',
                fontSize: '0.875rem',
                fontWeight: 500,
                cursor: 'pointer',
                marginRight: '0.5rem'
              }}
            >
              Cambiar ubicación
            </button>
            <button
              onClick={() => setShowAgregarModal(true)}
              style={{
                marginTop: '1rem',
                padding: '0.75rem 1.5rem',
                backgroundColor: colors.accent,
                color: '#fff',
                border: 'none',
                borderRadius: '10px',
                fontSize: '0.875rem',
                fontWeight: 500,
                cursor: 'pointer'
              }}
            >
              Agregar un lugar
            </button>
          </div>
        ) : userLocation && !loading && !error && lugares.length > 0 && (
          <>
            {/* Toast de error de favoritos */}
            {favoritoError && (
              <div style={{
                position: 'fixed',
                bottom: '1rem',
                left: '50%',
                transform: 'translateX(-50%)',
                backgroundColor: '#fef2f2',
                border: '1px solid #fecaca',
                color: '#991b1b',
                padding: '0.75rem 1rem',
                borderRadius: '8px',
                fontSize: '0.875rem',
                zIndex: 1000,
                maxWidth: '90%',
                textAlign: 'center'
              }}>
                {favoritoError}
              </div>
            )}

            {/* Mostrar establecimientos primero */}
            {establecimientos.map((lugar) => (
              <PlaceCard
                key={lugar.id}
                lugar={lugar}
                showActions={true}
                perfilId={perfilId}
                isFavorito={!!favoritos[lugar.id]}
                favoritoId={favoritos[lugar.id] || null}
                onToggleFavorite={handleToggleFavorite}
              />
            ))}

            {/* Luego proveedores independientes */}
            {proveedores.map((proveedor) => (
              <ProviderCard
                key={proveedor.id}
                proveedor={proveedor}
                showActions={true}
                perfilId={perfilId}
              />
            ))}

            {/* Botón para agregar lugar */}
            <button
              onClick={() => setShowAgregarModal(true)}
              style={{
                width: '100%',
                padding: '0.875rem',
                marginTop: '1rem',
                backgroundColor: colors.surface,
                border: `1px dashed ${colors.border}`,
                borderRadius: '10px',
                fontSize: '0.875rem',
                color: colors.textSecondary,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '0.5rem'
              }}
            >
              <span style={{ fontSize: '1.125rem' }}>+</span>
              Agregar un lugar que conozco
            </button>
          </>
        )}

        {/* Modal para agregar lugar */}
        <AgregarLugarModal
          isOpen={showAgregarModal}
          onClose={() => setShowAgregarModal(false)}
          categoriaSugerida={nombreCategoria}
        />

        {/* Modal de ubicación */}
        <LocationPrompt
          isOpen={showLocationPrompt}
          onLocationSet={handleLocationSet}
          onClose={() => setShowLocationPrompt(false)}
        />
      </div>
    </div>
  );
}
